import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "wouter";
import { ChevronDown, ChevronRight, Folder, FolderKanban, Loader2, Plus } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  SidebarPinSortable,
  SidebarPinSortableRow,
} from "@/components/sidebar-pin-sortable";
import { useIsMobile } from "@/hooks/use-media-query";
import { useResizableHeight } from "@/hooks/use-resizable-height";
import { useProjects, type UseProjectsReturn } from "@/hooks/use-projects";
import { useSharedProjects } from "@/hooks/projects-data-context";
import type { SidebarPinOrderingApi } from "@/hooks/use-sidebar-pin-ordering";
import { getProjectChange, PROJECTS_CHANGED_EVENT } from "@/lib/project-events";
import type { Project } from "@/lib/project-types";
import { cn } from "@/lib/utils";

interface ProjectSidebarSectionProps {
  getToken: () => Promise<string>;
  /** Opens a project folder or an existing task's board chat. */
  onOpenProject: (projectId: string, taskId?: string) => void;
  /** Creates and opens a new task chat without first routing through the project workspace. */
  onCreateProjectTaskChat?: (project: Project) => void | Promise<void>;
  activeProjectId?: string | null;
  /** Drag-and-drop display ordering for the pinned project folders. */
  pinOrdering?: SidebarPinOrderingApi;
}

interface ProjectFolderProps {
  project: Project;
  onOpenProject: (projectId: string, taskId?: string) => void;
  onCreateProjectTaskChat?: (project: Project) => void | Promise<void>;
  activeProjectId?: string | null;
}

const PROJECTS_EXPANDED_STORAGE_KEY = "sidebar-projects-expanded";
const PROJECTS_RESIZE_OPTIONS = {
  initial: 208,
  min: 64,
  max: 480,
  minPeer: 240,
  storageKey: "sidebar-projects-height",
} as const;

function ProjectFolder({
  project,
  onOpenProject,
  onCreateProjectTaskChat,
  activeProjectId,
}: ProjectFolderProps) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [creatingTaskChat, setCreatingTaskChat] = useState(false);
  const [taskChatCreationFailed, setTaskChatCreationFailed] = useState(false);
  const taskChatCreationInFlightRef = useRef(false);
  const isActive = project.id === activeProjectId;

  const handleCreateProjectTaskChat = useCallback(async () => {
    if (!onCreateProjectTaskChat || taskChatCreationInFlightRef.current) return;

    taskChatCreationInFlightRef.current = true;
    setCreatingTaskChat(true);
    setTaskChatCreationFailed(false);
    try {
      await onCreateProjectTaskChat(project);
    } catch {
      setTaskChatCreationFailed(true);
    } finally {
      taskChatCreationInFlightRef.current = false;
      setCreatingTaskChat(false);
    }
  }, [onCreateProjectTaskChat, project]);

  return (
    <SidebarPinSortableRow
      id={project.id}
      testId={`sidebar-project-${project.id}`}
      className={isActive ? "bg-muted/60" : "hover:bg-muted/60"}
      actions={(
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          data-testid={`project-folder-open-${project.id}`}
          aria-label={t("projects:addTask")}
          title={t("projects:addTask")}
          disabled={!onCreateProjectTaskChat || creatingTaskChat}
          onClick={() => { void handleCreateProjectTaskChat(); }}
          className={cn(
            isMobile
              ? "shrink-0 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100 focus-visible:opacity-100"
              : "pointer-events-auto shrink-0 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100 focus-visible:opacity-100",
            // Touch users cannot reveal a hover-only control. Keep the
            // task-creation action visibly available in the mobile drawer.
            isMobile && "opacity-100",
          )}
        >
          {creatingTaskChat
            ? <Loader2 className="size-3.5 animate-spin" aria-hidden="true" />
            : <Plus className="size-3.5" />}
        </Button>
      )}
      below={(
        <>
          {taskChatCreationFailed && (
            <div className="ml-5 flex items-center gap-2 border-l px-2 py-1 text-xs" role="alert">
              <span className="min-w-0 flex-1 text-destructive">{t("projects:taskChatCreateFailed")}</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 shrink-0 px-2 text-xs"
                onClick={() => { void handleCreateProjectTaskChat(); }}
              >
                {t("projects:retryTaskChatCreate")}
              </Button>
            </div>
          )}
        </>
      )}
    >
      <div
        data-testid={`project-folder-${project.id}`}
        className={cn("flex min-w-0 flex-1 items-center gap-1", !isMobile && "pr-16")}
      >
        <Button
          type="button"
          variant="ghost"
          onClick={() => onOpenProject(project.id)}
          className="h-auto min-w-0 flex-1 justify-start rounded-sm py-1 pl-0 pr-2 text-left font-normal hover:bg-muted"
        >
          <Folder className="size-4 shrink-0 text-primary" />
          <span className="truncate">{project.name}</span>
        </Button>
      </div>
    </SidebarPinSortableRow>
  );
}

/**
 * Project folders live alongside the normal session list. Opening a folder
 * navigates to its project workspace.
 */
export function ProjectSidebarSection(props: ProjectSidebarSectionProps) {
  const sharedProjects = useSharedProjects();

  if (sharedProjects) {
    return <ProjectSidebarSectionContent {...props} projectsApi={sharedProjects} shellOwned />;
  }
  return <StandaloneProjectSidebarSection {...props} />;
}

function StandaloneProjectSidebarSection(props: ProjectSidebarSectionProps) {
  const projectsApi = useProjects(props.getToken);
  return <ProjectSidebarSectionContent {...props} projectsApi={projectsApi} shellOwned={false} />;
}

function ProjectSidebarSectionContent({
  getToken,
  onOpenProject,
  onCreateProjectTaskChat,
  activeProjectId,
  projectsApi,
  shellOwned,
  pinOrdering,
}: ProjectSidebarSectionProps & { projectsApi: UseProjectsReturn; shellOwned: boolean }) {
  const { t } = useTranslation();
  const resize = useResizableHeight(PROJECTS_RESIZE_OPTIONS);
  const [expanded, setExpanded] = useState(() => {
    if (typeof window !== "undefined") {
      const saved = window.localStorage.getItem(PROJECTS_EXPANDED_STORAGE_KEY);
      if (saved === "1" || saved === "true") return true;
      if (saved === "0" || saved === "false") return false;
    }
    return true;
  });
  const { projects, loading, listError, refresh, upsert, remove } = projectsApi;
  const visibleProjects = projects;
  const displayProjects = useMemo(() => {
    if (!pinOrdering) return visibleProjects;
    const orderedIds = pinOrdering.displayIds(
      visibleProjects.map((project) => project.id),
      "projectIds",
    );
    const byId = new Map(visibleProjects.map((project) => [project.id, project]));
    return orderedIds.flatMap((id) => {
      const project = byId.get(id);
      return project ? [project] : [];
    });
  }, [pinOrdering, visibleProjects]);

  // Standalone renders retain local-hook synchronisation. Inside the shell,
  // ProjectsDataProvider already owns confirmed API changes.
  useEffect(() => {
    const handleProjectsChanged = (event: Event) => {
      const change = getProjectChange(event);
      if (!change) {
        if (!shellOwned) void refresh();
        return;
      }

      if (shellOwned) return;

      if (change.type === "remove") {
        remove(change.projectId);
      } else {
        upsert(change.project);
      }
    };
    window.addEventListener(PROJECTS_CHANGED_EVENT, handleProjectsChanged);
    return () => window.removeEventListener(PROJECTS_CHANGED_EVENT, handleProjectsChanged);
  }, [refresh, remove, shellOwned, upsert]);

  return (
    <section className="relative shrink-0 border-b pl-2 pt-2" aria-label={t("projects:sectionProjects")}>
      <div className="flex items-center justify-between gap-2 pl-1 pr-3 py-1 last:pb-3 text-xs font-medium text-muted-foreground">
        <div className="flex min-w-0 items-center gap-1">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  className="size-6 shrink-0"
                  data-testid="sidebar-projects-toggle"
                  aria-expanded={expanded}
                  aria-label={expanded ? t("projects:collapseProjects") : t("projects:expandProjects")}
                  onClick={() => setExpanded((current) => {
                    const next = !current;
                    try {
                      window.localStorage.setItem(PROJECTS_EXPANDED_STORAGE_KEY, String(next));
                    } catch {
                      /* localStorage may be unavailable (private mode) — ignore */
                    }
                    return next;
                  })}
                >
                  {expanded ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">
                {expanded ? t("projects:collapseProjects") : t("projects:expandProjects")}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <h3 className="flex min-w-0 items-center px-1 text-left text-xs font-medium text-muted-foreground">
            <span className="truncate">{t("projects:sectionProjects")}</span>
          </h3>
        </div>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                asChild
                variant="ghost"
                size="icon-sm"
                data-testid="sidebar-projects-view"
                className="size-5 p-0"
                aria-label={t("projects:viewAllProjects")}
              >
                <Link href="/projects/list">
                  <FolderKanban className="size-3.5" />
                </Link>
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">{t("projects:viewAllProjects")}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {expanded && (
        <div
          className="flex min-h-0 flex-col"
          style={{ maxHeight: resize.height }}
          data-testid="sidebar-projects-resizable-content"
        >
          <div className="min-h-0 flex-1 overflow-y-auto pr-2 pb-1">
            {loading && visibleProjects.length === 0 && (
              <div className="flex items-center gap-2 pl-2 pr-2 py-2 text-xs text-muted-foreground">
                <Loader2 className="size-3.5 animate-spin" />
                {t("projects:loadingProjects")}
              </div>
            )}
            {!loading && listError && (
              <div className="flex items-center gap-2 pl-2 pr-2 py-2 text-xs text-destructive">
                <span className="min-w-0 flex-1">{t("projects:projectLoadFailed")}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 shrink-0 px-2 text-xs"
                  onClick={() => { void refresh(); }}
                >
                  {t("common:actions.retry")}
                </Button>
              </div>
            )}
            {!loading && !listError && visibleProjects.length === 0 && (
              <p className="pl-2 pr-2 py-2 text-xs text-muted-foreground">{t("projects:noProjects")}</p>
            )}
            {pinOrdering?.persistError && (
              <p className="pl-2 pr-2 pb-1 text-xs text-muted-foreground" data-testid="sidebar-pin-order-notice">
                {t("projects:pinOrderUnavailable")}
              </p>
            )}
            {displayProjects.length > 0 && (
              <SidebarPinSortable
                ids={displayProjects.map((project) => project.id)}
                onReorder={(orderedIds) => pinOrdering?.reorder("projectIds", orderedIds)}
                className="space-y-0.5"
              >
                {displayProjects.map((project) => (
                  <ProjectFolder
                    key={project.id}
                    project={project}
                    onOpenProject={onOpenProject}
                    onCreateProjectTaskChat={onCreateProjectTaskChat}
                    activeProjectId={activeProjectId}
                  />
                ))}
              </SidebarPinSortable>
            )}
          </div>
          <div
            role="separator"
            aria-orientation="horizontal"
            aria-label={t("projects:resizeProjectsSection")}
            onPointerDown={resize.onPointerDown}
            onPointerMove={resize.onPointerMove}
            onPointerUp={resize.onPointerUp}
            onDoubleClick={resize.onDoubleClick}
            className="absolute inset-x-0 -bottom-1.5 z-10 h-3 cursor-row-resize touch-none select-none"
            data-testid="sidebar-projects-resize-handle"
          />
        </div>
      )}
    </section>
  );
}
